<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <div class="row">
          <div class="col-sm-6">
            <h3>Transfer Details</h3>
            <ol class="breadcrumb">
              <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
              <li class="breadcrumb-item"><router-link to="/owner/transfers">Transfers</router-link></li>
              <li class="breadcrumb-item active">{{ transfer?.transfer_number }}</li>
            </ol>
          </div>
          <div class="col-sm-6 text-end">
            <router-link to="/owner/transfers" class="btn btn-outline-primary">
              <vue-feather type="arrow-left" size="16"></vue-feather> Back to List
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
      </div>

      <div v-else-if="transfer">
        <div class="row">
          <!-- Transfer Info -->
          <div class="col-lg-8">
            <div class="card mb-3">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">{{ transfer.transfer_number }}</h5>
                <span class="badge" :class="getStatusBadgeClass(transfer.status)">
                  {{ formatStatus(transfer.status) }}
                </span>
              </div>
              <div class="card-body">
                <div class="row mb-4">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <small class="text-muted">From Branch</small>
                      <h5>{{ transfer.source_outlet?.name }}</h5>
                      <p class="text-muted mb-0">{{ transfer.source_outlet?.address }}</p>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <small class="text-muted">To Branch</small>
                      <h5>{{ transfer.destination_outlet?.name }}</h5>
                      <p class="text-muted mb-0">{{ transfer.destination_outlet?.address }}</p>
                    </div>
                  </div>
                </div>

                <div v-if="transfer.notes" class="mb-4">
                  <small class="text-muted">Notes</small>
                  <p>{{ transfer.notes }}</p>
                </div>

                <!-- Items Table -->
                <h6 class="mb-3">Transfer Items</h6>
                <div class="table-responsive">
                  <table class="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th class="text-center">Requested</th>
                        <th class="text-center">Sent</th>
                        <th class="text-center">Received</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in transfer.items" :key="item.id">
                        <td>
                          <strong>{{ item.product?.name }}</strong>
                          <br><small class="text-muted">{{ item.product?.sku }}</small>
                        </td>
                        <td class="text-center">{{ item.quantity_requested }}</td>
                        <td class="text-center">
                          <span v-if="transfer.status === 'in_transit' || transfer.status === 'received'">
                            {{ item.quantity_sent }}
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-center">
                          <span v-if="transfer.status === 'received'">
                            {{ item.quantity_received }}
                            <span v-if="item.quantity_received !== item.quantity_sent" class="text-warning">
                              ({{ item.quantity_received - item.quantity_sent > 0 ? '+' : '' }}{{ item.quantity_received - item.quantity_sent }})
                            </span>
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td><small>{{ item.notes || '-' }}</small></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="card" v-if="canTakeAction">
              <div class="card-body">
                <h6 class="mb-3">Actions</h6>

                <!-- Ship Form -->
                <div v-if="transfer.status === 'approved'">
                  <p>Confirm quantities being shipped:</p>
                  <div class="table-responsive mb-3">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Requested</th>
                          <th>Quantity to Ship</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in transfer.items" :key="item.id">
                          <td>{{ item.product?.name }}</td>
                          <td>{{ item.quantity_requested }}</td>
                          <td style="width: 120px;">
                            <input type="number" class="form-control form-control-sm" v-model="shipQuantities[item.product_id]" :max="item.quantity_requested" min="0">
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <button class="btn btn-primary" @click="shipTransfer" :disabled="processing">
                    <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
                    <vue-feather type="truck" size="16" class="me-1"></vue-feather>
                    Ship Transfer
                  </button>
                </div>

                <!-- Receive Form -->
                <div v-if="transfer.status === 'in_transit'">
                  <p>Confirm quantities received:</p>
                  <div class="table-responsive mb-3">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Sent</th>
                          <th>Quantity Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in transfer.items" :key="item.id">
                          <td>{{ item.product?.name }}</td>
                          <td>{{ item.quantity_sent }}</td>
                          <td style="width: 120px;">
                            <input type="number" class="form-control form-control-sm" v-model="receiveQuantities[item.product_id]" :max="item.quantity_sent" min="0">
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <button class="btn btn-success" @click="receiveTransfer" :disabled="processing">
                    <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
                    <vue-feather type="check-circle" size="16" class="me-1"></vue-feather>
                    Confirm Receipt
                  </button>
                </div>

                <!-- Approve Button -->
                <div v-if="transfer.status === 'pending'">
                  <button class="btn btn-success me-2" @click="approveTransfer" :disabled="processing">
                    <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
                    <vue-feather type="check" size="16" class="me-1"></vue-feather>
                    Approve Transfer
                  </button>
                  <button class="btn btn-danger" @click="showCancelModal">
                    <vue-feather type="x" size="16" class="me-1"></vue-feather>
                    Cancel Transfer
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Timeline -->
          <div class="col-lg-4">
            <div class="card">
              <div class="card-header">
                <h5 class="mb-0">Timeline</h5>
              </div>
              <div class="card-body">
                <ul class="list-unstyled timeline-widget mb-0">
                  <li class="timeline-item" v-if="transfer.created_at">
                    <div class="d-flex">
                      <div class="flex-shrink-0 me-3">
                        <span class="badge rounded-pill bg-primary">
                          <vue-feather type="file-plus" size="12"></vue-feather>
                        </span>
                      </div>
                      <div class="flex-grow-1">
                        <strong>Created</strong>
                        <p class="mb-0 text-muted">
                          {{ formatDateTime(transfer.created_at) }}
                          <br><small>by {{ transfer.creator?.name }}</small>
                        </p>
                      </div>
                    </div>
                  </li>
                  <li class="timeline-item" v-if="transfer.approved_at">
                    <div class="d-flex">
                      <div class="flex-shrink-0 me-3">
                        <span class="badge rounded-pill bg-success">
                          <vue-feather type="check" size="12"></vue-feather>
                        </span>
                      </div>
                      <div class="flex-grow-1">
                        <strong>Approved</strong>
                        <p class="mb-0 text-muted">
                          {{ formatDateTime(transfer.approved_at) }}
                          <br><small>by {{ transfer.approver?.name }}</small>
                        </p>
                      </div>
                    </div>
                  </li>
                  <li class="timeline-item" v-if="transfer.shipped_at">
                    <div class="d-flex">
                      <div class="flex-shrink-0 me-3">
                        <span class="badge rounded-pill bg-info">
                          <vue-feather type="truck" size="12"></vue-feather>
                        </span>
                      </div>
                      <div class="flex-grow-1">
                        <strong>Shipped</strong>
                        <p class="mb-0 text-muted">
                          {{ formatDateTime(transfer.shipped_at) }}
                          <br><small>by {{ transfer.shipper?.name }}</small>
                        </p>
                      </div>
                    </div>
                  </li>
                  <li class="timeline-item" v-if="transfer.received_at">
                    <div class="d-flex">
                      <div class="flex-shrink-0 me-3">
                        <span class="badge rounded-pill bg-success">
                          <vue-feather type="check-circle" size="12"></vue-feather>
                        </span>
                      </div>
                      <div class="flex-grow-1">
                        <strong>Received</strong>
                        <p class="mb-0 text-muted">
                          {{ formatDateTime(transfer.received_at) }}
                          <br><small>by {{ transfer.receiver?.name }}</small>
                        </p>
                      </div>
                    </div>
                  </li>
                  <li class="timeline-item" v-if="transfer.cancelled_at">
                    <div class="d-flex">
                      <div class="flex-shrink-0 me-3">
                        <span class="badge rounded-pill bg-danger">
                          <vue-feather type="x-circle" size="12"></vue-feather>
                        </span>
                      </div>
                      <div class="flex-grow-1">
                        <strong>Cancelled</strong>
                        <p class="mb-0 text-muted">
                          {{ formatDateTime(transfer.cancelled_at) }}
                          <br><small>{{ transfer.cancel_reason }}</small>
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Modal -->
    <div class="modal fade" id="cancelModal" tabindex="-1" ref="cancelModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Cancel Transfer</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to cancel this transfer?</p>
            <div class="mb-3">
              <label class="form-label">Reason <span class="text-danger">*</span></label>
              <textarea class="form-control" v-model="cancelReason" rows="3" required></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-danger" @click="cancelTransfer" :disabled="!cancelReason || processing">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
              Cancel Transfer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ownerTransferApi } from '@/services/ownerApi';
import { Modal } from 'bootstrap';

export default {
  name: 'TransferDetail',

  data() {
    return {
      loading: true,
      processing: false,
      transfer: null,
      cancelReason: '',
      shipQuantities: {},
      receiveQuantities: {},
      cancelModalInstance: null
    };
  },

  computed: {
    canTakeAction() {
      return ['pending', 'approved', 'in_transit'].includes(this.transfer?.status);
    }
  },

  mounted() {
    this.fetchTransfer();
    this.cancelModalInstance = new Modal(this.$refs.cancelModal);
  },

  methods: {
    formatDateTime(dateStr) {
      return new Date(dateStr).toLocaleString('en-MY', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatStatus(status) {
      const statuses = {
        pending: 'Pending',
        approved: 'Approved',
        in_transit: 'In Transit',
        received: 'Received',
        cancelled: 'Cancelled'
      };
      return statuses[status] || status;
    },

    getStatusBadgeClass(status) {
      const classes = {
        pending: 'bg-warning',
        approved: 'bg-info',
        in_transit: 'bg-primary',
        received: 'bg-success',
        cancelled: 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    async fetchTransfer() {
      try {
        this.loading = true;
        const response = await ownerTransferApi.get(this.$route.params.id);
        this.transfer = response.data.data;

        // Initialize quantities
        this.transfer.items.forEach(item => {
          this.shipQuantities[item.product_id] = item.quantity_requested;
          this.receiveQuantities[item.product_id] = item.quantity_sent || item.quantity_requested;
        });
      } catch (error) {
        console.error('Failed to fetch transfer:', error);
        this.$router.push('/owner/transfers');
      } finally {
        this.loading = false;
      }
    },

    async approveTransfer() {
      try {
        this.processing = true;
        await ownerTransferApi.approve(this.transfer.id);
        this.fetchTransfer();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to approve transfer');
      } finally {
        this.processing = false;
      }
    },

    async shipTransfer() {
      try {
        this.processing = true;
        await ownerTransferApi.ship(this.transfer.id, this.shipQuantities);
        this.fetchTransfer();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to ship transfer');
      } finally {
        this.processing = false;
      }
    },

    async receiveTransfer() {
      try {
        this.processing = true;
        await ownerTransferApi.receive(this.transfer.id, this.receiveQuantities);
        this.fetchTransfer();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to receive transfer');
      } finally {
        this.processing = false;
      }
    },

    showCancelModal() {
      this.cancelReason = '';
      this.cancelModalInstance.show();
    },

    async cancelTransfer() {
      if (!this.cancelReason) return;

      try {
        this.processing = true;
        await ownerTransferApi.cancel(this.transfer.id, this.cancelReason);
        this.cancelModalInstance.hide();
        this.fetchTransfer();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to cancel transfer');
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
.timeline-item {
  padding-bottom: 1rem;
  border-left: 2px solid #e9ecef;
  padding-left: 1.5rem;
  margin-left: 0.5rem;
}
.timeline-item:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}
</style>
